/**
 * Explainer Service - AI-powered slide deck generation
 * 
 * Connects to Content Brain API endpoints for:
 * - Generating slide deck outlines (with AI thinking)
 * - Generating individual slides with tutor prompts
 * - Image search and generation
 */

import { supabase } from '@/integrations/supabase/client';
import { config } from '@/lib/config';

// Re-export types for compatibility
export type { SlideOutline, SlideOutlineItem, SlideContent, GeneratedSlideDeck } from './mockSlideDeckService';

// API base URL - uses main backend (proxies to content-brain)
const API_BASE_URL = config.BACKEND_API_URL;

/**
 * Generate an explainer outline using AI
 * 
 * @param topic - Topic to explain
 * @param durationMinutes - Target duration in minutes (5-20)
 * @param journalId - Optional journal ID for context grounding
 * @returns Slide outline with structure and descriptions
 */
export async function generateOutline(
    topic: string,
    durationMinutes: number,
    journalId?: string
): Promise<import('./mockSlideDeckService').SlideOutline> {
    // Get auth token
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    const response = await fetch(`${API_BASE_URL}/api/journals/generate-explainer-outline`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        body: JSON.stringify({
            topic,
            duration_minutes: durationMinutes,
            journal_id: journalId,
        }),
    });

    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to generate outline: ${response.status} - ${errorText}`);
    }

    return response.json();
}

/**
 * Refine an existing outline based on user feedback
 * 
 * Uses AI to intelligently interpret feedback like "make it longer",
 * "more comprehensive", "simpler", etc.
 * 
 * @param originalTopic - The original topic request
 * @param currentOutline - The current outline to refine
 * @param refinementRequest - User's feedback/refinement request
 * @param journalId - Optional journal ID for context
 * @returns Refined slide outline
 */
export async function refineOutline(
    originalTopic: string,
    currentOutline: import('./mockSlideDeckService').SlideOutline,
    refinementRequest: string,
    journalId?: string
): Promise<import('./mockSlideDeckService').SlideOutline> {
    // Get auth token
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    const response = await fetch(`${API_BASE_URL}/api/journals/refine-explainer-outline`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        body: JSON.stringify({
            original_topic: originalTopic,
            current_outline: currentOutline,
            refinement_request: refinementRequest,
            journal_id: journalId,
        }),
    });

    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to refine outline: ${response.status} - ${errorText}`);
    }

    return response.json();
}

/**
 * Generate a single slide from the outline
 * 
 * @param outline - The deck outline
 * @param slideIndex - Index of slide to generate (0-based)
 * @param previousSlides - Previously generated slides for coherence
 * @param journalId - Optional journal ID for context
 * @returns Generated slide content with tutor_prompt
 */
export async function generateSlide(
    outline: import('./mockSlideDeckService').SlideOutline,
    slideIndex: number,
    previousSlides: import('./mockSlideDeckService').SlideContent[] = [],
    journalId?: string
): Promise<import('./mockSlideDeckService').SlideContent> {
    // Get auth token
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    const response = await fetch(`${API_BASE_URL}/api/journals/generate-explainer-slide`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        body: JSON.stringify({
            outline,
            slide_index: slideIndex,
            previous_slides: previousSlides,
            journal_id: journalId,
        }),
    });

    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to generate slide: ${response.status} - ${errorText}`);
    }

    return response.json();
}

/**
 * Batch response type from parallel slide generation
 */
export interface BatchSlideResponse {
    slides: Record<string, import('./mockSlideDeckService').SlideContent>;
    errors: Record<string, string>;
    total_requested: number;
    total_succeeded: number;
}

/**
 * Generate multiple slides in parallel via backend batch endpoint
 * 
 * @param outline - The deck outline
 * @param slideIndices - Indices of slides to generate
 * @param previousSlides - Slides completed before this batch
 * @param journalId - Optional journal ID for context
 * @param maxConcurrent - Max concurrent generations (default 3)
 * @returns Batch response with slides and errors
 */
export async function batchGenerateSlides(
    outline: import('./mockSlideDeckService').SlideOutline,
    slideIndices: number[],
    previousSlides: import('./mockSlideDeckService').SlideContent[] = [],
    journalId?: string,
    maxConcurrent: number = 3
): Promise<BatchSlideResponse> {
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    const response = await fetch(`${API_BASE_URL}/api/journals/batch-generate-explainer-slides`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        body: JSON.stringify({
            outline,
            slide_indices: slideIndices,
            previous_slides: previousSlides,
            journal_id: journalId,
            max_concurrent: maxConcurrent,
        }),
    });

    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to batch generate slides: ${response.status} - ${errorText}`);
    }

    return response.json();
}

/**
 * Generate slides in parallel with ordered yielding using Promise.race
 * 
 * Uses aggressive parallelism (3 concurrent) with instant response via Promise.race.
 * Slides are yielded in order for proper display, with a sliding window of requests.
 * 
 * @param outline - The deck outline  
 * @param journalId - Optional journal ID for context
 * @param concurrency - Max concurrent generations (default 3)
 * @yields SlideContent in order as they complete
 */
export async function* generateSlidesParallel(
    outline: import('./mockSlideDeckService').SlideOutline,
    journalId?: string,
    concurrency: number = 3
): AsyncGenerator<{
    slide: import('./mockSlideDeckService').SlideContent;
    index: number;
    total: number;
    isComplete: boolean;
}> {
    const totalSlides = outline.slides.length;
    const results = new Map<number, import('./mockSlideDeckService').SlideContent>();
    const inFlight = new Map<number, Promise<{ index: number; slide: import('./mockSlideDeckService').SlideContent | null }>>();
    const completedSlides: import('./mockSlideDeckService').SlideContent[] = [];
    let nextToYield = 0;
    let nextToStart = 0;

    // Helper: Start a slide request and track it
    const startSlide = (idx: number) => {
        const promise = generateSlide(outline, idx, completedSlides.slice(), journalId)
            .then(slide => {
                results.set(idx, slide);
                inFlight.delete(idx);  // Remove from in-flight when done
                return { index: idx, slide };
            })
            .catch(error => {
                console.error(`Failed to generate slide ${idx}:`, error);
                inFlight.delete(idx);  // Remove from in-flight on error too
                return { index: idx, slide: null };
            });
        inFlight.set(idx, promise);
    };

    // Fire initial concurrent requests immediately
    while (nextToStart < Math.min(concurrency, totalSlides)) {
        startSlide(nextToStart);
        nextToStart++;
    }

    // Process completions using Promise.race for instant response
    while (nextToYield < totalSlides) {
        // If we're waiting for a specific slide and it's already done, yield it
        if (results.has(nextToYield)) {
            const slide = results.get(nextToYield)!;
            completedSlides.push(slide);

            yield {
                slide,
                index: nextToYield,
                total: totalSlides,
                isComplete: nextToYield === totalSlides - 1
            };

            nextToYield++;

            // Refill the pool with next slide
            if (nextToStart < totalSlides && inFlight.size < concurrency) {
                startSlide(nextToStart);
                nextToStart++;
            }
            continue;
        }

        // Wait for ANY in-flight request to complete using Promise.race
        if (inFlight.size > 0) {
            await Promise.race(inFlight.values());
            // Promise removed itself from inFlight in .then handler
            // Loop will check if nextToYield is ready on next iteration
        } else if (nextToStart < totalSlides) {
            // No in-flight but more to start - start the next one
            startSlide(nextToStart);
            nextToStart++;
        } else {
            // No in-flight, nothing to start, but nextToYield not ready - shouldn't happen
            console.error('Generator stuck: no in-flight requests, waiting for slide', nextToYield);
            break;
        }
    }
}

/**
 * Generate slides in parallel, yielding as they complete (fastest first)
 * 
 * Unlike generateSlidesParallel which yields in order, this yields each slide
 * immediately when it completes regardless of order. This gives the fastest
 * perceived response time - users see content appear quickly.
 * 
 * @param outline - The deck outline  
 * @param journalId - Optional journal ID for context
 * @param concurrency - Max concurrent generations (default 3)
 * @yields SlideContent as each completes (may be out of order)
 */
export async function* generateSlidesFastestFirst(
    outline: import('./mockSlideDeckService').SlideOutline,
    journalId?: string,
    concurrency: number = 3
): AsyncGenerator<{
    slide: import('./mockSlideDeckService').SlideContent;
    index: number;
    total: number;
    isComplete: boolean;
}> {
    const totalSlides = outline.slides.length;
    const completedSlides: import('./mockSlideDeckService').SlideContent[] = [];
    const inFlight = new Map<number, Promise<{ index: number; slide: import('./mockSlideDeckService').SlideContent | null }>>();
    let nextToStart = 0;
    let yieldedCount = 0;

    // Helper: Start a slide request
    const startSlide = (idx: number) => {
        const promise = generateSlide(outline, idx, completedSlides.slice(), journalId)
            .then(slide => {
                completedSlides.push(slide);
                inFlight.delete(idx);
                return { index: idx, slide };
            })
            .catch(error => {
                console.error(`Failed to generate slide ${idx}:`, error);
                inFlight.delete(idx);
                return { index: idx, slide: null };
            });
        inFlight.set(idx, promise);
        return promise;
    };

    // Fire initial concurrent requests
    while (nextToStart < Math.min(concurrency, totalSlides)) {
        startSlide(nextToStart);
        nextToStart++;
    }

    // Yield slides as they complete (fastest first, any order)
    while (yieldedCount < totalSlides) {
        if (inFlight.size > 0) {
            // Wait for any slide to complete
            const completed = await Promise.race(inFlight.values());

            if (completed.slide) {
                yield {
                    slide: completed.slide,
                    index: completed.index,
                    total: totalSlides,
                    isComplete: yieldedCount + 1 === totalSlides
                };
                yieldedCount++;
            }

            // Refill the pool
            if (nextToStart < totalSlides && inFlight.size < concurrency) {
                startSlide(nextToStart);
                nextToStart++;
            }
        } else if (nextToStart < totalSlides) {
            startSlide(nextToStart);
            nextToStart++;
        } else {
            break;
        }
    }
}

/**
 * Image search result from Content Brain
 */
export interface ImageSearchResult {
    url: string;
    alt: string;
    author?: string;
    license?: string;
    source_url?: string;
}

/**
 * Search for images via Wikimedia Commons (for async image resolution)
 * 
 * @param query - Search query (2-4 keywords work best)
 * @param journalId - Journal ID for authentication
 * @returns Image result with URL and attribution
 */
export async function searchImage(
    query: string,
    journalId: string
): Promise<ImageSearchResult | null> {
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    try {
        const response = await fetch(`${API_BASE_URL}/api/journals/search-image`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(token && { 'Authorization': `Bearer ${token}` }),
            },
            body: JSON.stringify({
                query,
                count: 1,
                journal_id: journalId,
            }),
        });

        if (!response.ok) {
            console.warn(`Image search failed: ${response.status}`);
            return null;
        }

        const data = await response.json();
        // Backend returns { images: [...] } from ImageSearchResponse
        if (data.images && data.images.length > 0) {
            return data.images[0];
        }
        return null;
    } catch (error) {
        console.error('Image search error:', error);
        return null;
    }
}

/**
 * Async generator that yields slides as they are generated
 * 
 * This provides progressive loading - slides appear one by one
 * as they are generated by the AI.
 * 
 * @param outline - The deck outline
 * @param topic - Topic string (used for context)
 * @param journalId - Optional journal ID for context
 * @yields SlideContent as each slide is generated
 */
export async function* generateSlidesStream(
    outline: import('./mockSlideDeckService').SlideOutline,
    topic: string,
    journalId?: string
): AsyncGenerator<import('./mockSlideDeckService').SlideContent> {
    const previousSlides: import('./mockSlideDeckService').SlideContent[] = [];

    for (let i = 0; i < outline.slides.length; i++) {
        const slide = await generateSlide(outline, i, previousSlides, journalId);
        previousSlides.push(slide);
        yield slide;
    }
}

/**
 * Search for images via Unsplash
 */
export async function searchImages(query: string, count: number = 1, journalId?: string): Promise<Array<{
    url: string;
    alt: string;
    photographer?: string;
}>> {
    // Get auth token
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    const response = await fetch(`${API_BASE_URL}/api/journals/search-image`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        body: JSON.stringify({ query, count, journal_id: journalId }),
    });

    if (!response.ok) {
        throw new Error('Image search failed');
    }

    const data = await response.json();
    return data.images;
}

/**
 * Generate an image using AI (Nano Banana)
 */
export async function generateImage(
    prompt: string,
    style: 'illustration' | 'diagram' | 'photo' | 'educational' = 'educational',
    journalId?: string
): Promise<{ url: string; alt: string }> {
    // Get auth token
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    const response = await fetch(`${API_BASE_URL}/api/journals/generate-image`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        body: JSON.stringify({ prompt, style, journal_id: journalId }),
    });

    if (!response.ok) {
        throw new Error('Image generation failed');
    }

    return response.json();
}

// =============================================================================
// Skeleton Flow Types & Functions (Two-Phase Generation)
// =============================================================================

/**
 * Lightweight skeleton item for quick preview
 */
export interface SlideSkeletonItem {
    id: string;
    order: number;
    title: string;
    type: 'title' | 'content' | 'diagram' | 'summary' | 'interactive';
}

/**
 * Skeleton outline for user review before enrichment
 */
export interface SlideSkeletonOutline {
    title: string;
    description: string;
    estimatedDuration: number;
    slideCount: number;
    slides: SlideSkeletonItem[];
}

/**
 * Response from accepting an outline (includes enriched full outline)
 */
export interface AcceptOutlineResponse {
    outline: import('./mockSlideDeckService').SlideOutline;
    generation_started: boolean;
}

/**
 * Generate a lightweight skeleton outline for quick user review
 * 
 * Phase 1 of two-phase generation - fast, no pedagogical depth.
 * User refines this skeleton, then accepts to trigger enrichment.
 * 
 * @param topic - Topic to explain
 * @param durationMinutes - Target duration in minutes (5-20)
 * @param journalId - Optional journal ID for context grounding
 * @returns Skeleton outline for user review
 */
export async function generateSkeleton(
    topic: string,
    durationMinutes: number,
    journalId?: string
): Promise<SlideSkeletonOutline> {
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    const response = await fetch(`${API_BASE_URL}/api/journals/generate-explainer-skeleton`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        body: JSON.stringify({
            topic,
            duration_minutes: durationMinutes,
            journal_id: journalId,
        }),
    });

    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to generate skeleton: ${response.status} - ${errorText}`);
    }

    return response.json();
}

/**
 * Refine a skeleton outline based on user feedback
 * 
 * Lightweight refinement - only structure changes, no pedagogy.
 * Fast response for quick iteration.
 * 
 * @param originalTopic - The original topic request
 * @param currentSkeleton - The current skeleton to refine
 * @param refinementRequest - User's feedback/refinement request
 * @param journalId - Optional journal ID for context
 * @returns Refined skeleton outline
 */
export async function refineSkeleton(
    originalTopic: string,
    currentSkeleton: SlideSkeletonOutline,
    refinementRequest: string,
    journalId?: string
): Promise<SlideSkeletonOutline> {
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    const response = await fetch(`${API_BASE_URL}/api/journals/refine-explainer-skeleton`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        body: JSON.stringify({
            original_topic: originalTopic,
            current_skeleton: currentSkeleton,
            refinement_request: refinementRequest,
            journal_id: journalId,
        }),
    });

    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to refine skeleton: ${response.status} - ${errorText}`);
    }

    return response.json();
}

/**
 * Accept a skeleton outline and trigger enrichment
 * 
 * Phase 2 of two-phase generation:
 * - Enriches skeleton with full pedagogical metadata
 * - Returns complete SlideOutline ready for slide generation
 * 
 * @param skeleton - The accepted skeleton outline
 * @param topic - Original topic for context
 * @param journalId - Optional journal ID for context
 * @returns Full enriched outline and generation status
 */
export async function acceptOutline(
    skeleton: SlideSkeletonOutline,
    topic: string,
    journalId?: string
): Promise<AcceptOutlineResponse> {
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    const response = await fetch(`${API_BASE_URL}/api/journals/accept-explainer-outline`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        body: JSON.stringify({
            skeleton,
            topic,
            journal_id: journalId,
        }),
    });

    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to accept outline: ${response.status} - ${errorText}`);
    }

    return response.json();
}
